import { type AccountModel, PositionModel, type Prices, UtilsService } from "../index"
import type InstrumentModel from "../models/instrument.model"
import { AccountsController } from "../index"
import type { BalanceModel } from "../models/balance.model"
import Decimal from "decimal.js-light"

type ContractSizes = Record<string, string | number>
type ConversionRates = Record<string, string | number>

const instrumentKey = (symbol: string) => symbol.replace(/^#/, '')

export default class AccountService {
  constructor(private controller: AccountsController) {
  }

  static calculateConversionRate(
    symbol: string,
    accountCurrency: string | undefined,
    instruments: InstrumentModel[],
    prices: Prices
  ): number {
    const normalizedSymbol = instrumentKey(symbol)
    const account = accountCurrency?.toUpperCase()
    const instrument = instruments.find((item) => instrumentKey(item.symbol) === normalizedSymbol)
    const quoteCurrency = instrument?.quote_currency?.toUpperCase()

    if (!account || !quoteCurrency || quoteCurrency === account) return 1

    const direct = instruments.find(
      (item) =>
        item.is_active &&
        item.base_currency?.toUpperCase() === quoteCurrency &&
        item.quote_currency?.toUpperCase() === account
    )
    const reverse = instruments.find(
      (item) =>
        item.is_active &&
        item.base_currency?.toUpperCase() === account &&
        item.quote_currency?.toUpperCase() === quoteCurrency
    )
    const directPrice = direct ? prices[direct.symbol] : undefined
    const reversePrice = reverse ? prices[reverse.symbol] : undefined

    return typeof directPrice === 'number' && directPrice > 0
      ? directPrice
      : typeof reversePrice === 'number' && reversePrice > 0
        ? 1 / reversePrice
        : 1
  }

  static calculatePnl(
    positions: PositionModel[],
    prices: Prices,
    contractSizes: ContractSizes = {},
    conversionRates: ConversionRates = {},
    marketPrices: Prices = {}
  ): Decimal {
    return positions.reduce((acc, pos) => {
      const contractSize = contractSizes[pos.symbol] ?? contractSizes[instrumentKey(pos.symbol)] ?? 1
      const conversionRate =
        conversionRates[pos.symbol] ?? conversionRates[instrumentKey(pos.symbol)] ?? 0

      if (conversionRate === 0) {
        const unrealizedPnl = new Decimal(pos.unrealized_pnl)
        return !unrealizedPnl.isZero() ? unrealizedPnl : new Decimal(0)
      }

      const pnl = AccountService.calculatePositionPnl(
        pos,
        prices,
        contractSize,
        conversionRate,
        marketPrices[pos.symbol] ?? marketPrices[instrumentKey(pos.symbol)]
      )

      return acc.plus(pnl)
    }, new Decimal(0))
  }

  static calculatePositionPnl(
    pos: PositionModel,
    prices: Prices,
    contractSize: string | number = 1,
    conversionRate: string | number = 1,
    marketPrice?: string | number
  ): Decimal {
    const price = UtilsService.toDecimal(
      marketPrice ?? prices[pos.symbol] ?? prices[instrumentKey(pos.symbol)]
    )

    if (price.eq(0)) {
      return new Decimal(0)
    }

    const entry = UtilsService.toDecimal(
      pos.entry_price
    )

    const qty = UtilsService.toDecimal(
      pos.quantity
    )

    const quantityUnits = qty.mul(UtilsService.toDecimal(contractSize, 1))

    const result = pos.side === 'LONG'
      ? price.minus(entry).mul(quantityUnits)
      : entry.minus(price).mul(quantityUnits)
    const convertedResult = result.mul(UtilsService.toDecimal(conversionRate, 1))

    return Math.abs(convertedResult.mul(1000).toNumber()) < 5
      ? new Decimal(0)
      : convertedResult
  }

  static calculateEquity(balance: Decimal, pl: Decimal): Decimal {
    return balance.plus(pl)
  }

  static calculateMarginLevel(
    equity: Decimal,
    margin: Decimal
  ): Decimal {
    if (margin.isZero()) {
      return margin
    }

    return equity
      .div(margin)
      .mul(100)
  }

  async getAccounts(): Promise<AccountModel[]> {
    return this.controller.getAccounts()
  }

  async getBalances(): Promise<BalanceModel[]> {
    return this.controller.getBalances()
  }
}
